import AddLinkOutlinedIcon from "@mui/icons-material/AddLinkOutlined";
import CheckOutlinedIcon from "@mui/icons-material/CheckOutlined";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import KeyOutlinedIcon from "@mui/icons-material/KeyOutlined";
import LinkOffOutlinedIcon from "@mui/icons-material/LinkOffOutlined";
import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";
import NotificationsActiveOutlinedIcon from "@mui/icons-material/NotificationsActiveOutlined";
import SecurityOutlinedIcon from "@mui/icons-material/SecurityOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import { Box, Button, Chip, IconButton, Skeleton, Tooltip, Typography } from "@mui/material";
import { useState } from "react";
import { BaseModalComponent } from "../common/BaseModalComponent";
import { WidgetComponentMaps } from "../common/WidgetComponentMaps";
import { colors } from "../ui/colors";
import { brandButton, filledButton } from "../ui/styles/buttonStyles";
import { EventDeliveryManage } from "./EventDeliveryManage";
import { useGetEventDeliveryConfig } from "./hooks/useGetEventDeliveryConfig";

export interface IEventDeliveryWidgetProps {
  swapyName?: string;
  sx?: any;
}

export const EventDeliveryWidget = ({ swapyName = "eventDeliveryWidget", sx }: IEventDeliveryWidgetProps) => {
  const { data: config, isLoading } = useGetEventDeliveryConfig();
  const isEnabled = Boolean(config?.isEnabled);
  const isRegistered = Boolean(config?.uid);
  const [copied, setCopied] = useState(false);

  const handleCopyUrl = async () => {
    if (!config?.webhookUrl) return;
    try {
      await navigator.clipboard.writeText(config.webhookUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback
    }
  };

  return (
    <WidgetComponentMaps
      swapyName={swapyName}
      title="Event Delivery"
      description="Register webhooks to receive instant notifications for jobs, visits, and invoices."
      icon={<NotificationsActiveOutlinedIcon />}
      variant={isRegistered ? (isEnabled ? "success" : "warning") : "default"}
      sxProp={{
        ...sx,
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box sx={{ flex: 1, px: 0.5, pt: 1, pb: 0.5, width: "100%" }}>
        {isLoading ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <Skeleton variant="rounded" width="45%" height={26} sx={{ borderRadius: "12px" }} />
            <Skeleton variant="rounded" width="100%" height={56} sx={{ borderRadius: "12px" }} />
            <Skeleton variant="rounded" width="60%" height={24} sx={{ borderRadius: "8px" }} />
          </Box>
        ) : config && isRegistered ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {/* Status Chip & Auth indicator */}
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1 }}>
              <Chip
                size="small"
                label={isEnabled ? "Delivery Active" : "Delivery Paused"}
                icon={
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      ml: 1,
                      mr: -0.5,
                      bgcolor: isEnabled ? "#16a34a" : "#f59e0b",
                      boxShadow: isEnabled ? "0 0 0 3px rgba(34,197,94,0.25)" : "0 0 0 3px rgba(245,158,11,0.25)",
                    }}
                  />
                }
                sx={{
                  height: 28,
                  fontWeight: 700,
                  fontSize: 12,
                  borderRadius: "999px",
                  bgcolor: isEnabled ? "rgba(34, 197, 94, 0.1)" : "rgba(245, 158, 11, 0.1)",
                  color: isEnabled ? "#15803d" : "#b45309",
                  border: isEnabled ? "1px solid rgba(34, 197, 94, 0.25)" : "1px solid rgba(245, 158, 11, 0.3)",
                  px: 0.5,
                }}
              />
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {config.hasSigningSecret ? (
                  <Chip
                    size="small"
                    icon={<SecurityOutlinedIcon style={{ fontSize: 13 }} />}
                    label="HMAC Signed"
                    sx={{
                      height: 26,
                      fontWeight: 600,
                      fontSize: 11,
                      borderRadius: "8px",
                      bgcolor: "rgba(16, 185, 129, 0.08)",
                      color: "#059669",
                      border: "1px solid rgba(16, 185, 129, 0.2)",
                    }}
                  />
                ) : null}
                {config.hasAuthToken ? (
                  <Chip
                    size="small"
                    icon={<KeyOutlinedIcon style={{ fontSize: 13 }} />}
                    label="Bearer Token Set"
                    sx={{
                      height: 26,
                      fontWeight: 600,
                      fontSize: 11,
                      borderRadius: "8px",
                      bgcolor: "rgba(59, 130, 246, 0.08)",
                      color: "#2563eb",
                      border: "1px solid rgba(var(--fx-surface-textrgb, 15, 23, 42), 0.06)",
                    }}
                  />
                ) : null}
              </Box>
            </Box>

            {/* Webhook URL Container */}
            <Box
              sx={{
                bgcolor: colors.warmWhite.soft || "var(--fx-surface-sunken, #F8FAFC)",
                border: `1px solid ${colors.gray[200] || "rgba(var(--fx-surface-textrgb, 15, 23, 42), 0.08)"}`,
                borderRadius: "12px",
                p: 1.5,
                transition: "all 0.2s ease",
                "&:hover": {
                  borderColor: colors.orange[300] || "var(--fx-surface-borderstrong, #CBD5E1)",
                  bgcolor: "var(--fx-surface-base, #FFFFFF)",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: 11,
                    color: colors.gray[500] || "var(--fx-surface-textsecondary, #64748B)",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                  }}
                >
                  <LinkOutlinedIcon sx={{ fontSize: 14, color: colors.orange.base }} />
                  Webhook Endpoint
                </Typography>
                <Tooltip title={copied ? "Copied!" : "Copy Webhook URL"}>
                  <IconButton
                    size="small"
                    onClick={handleCopyUrl}
                    sx={{
                      p: 0.5,
                      color: copied ? "#16a34a" : colors.gray[500],
                      bgcolor: copied ? "rgba(34,197,94,0.1)" : "transparent",
                      "&:hover": { bgcolor: "rgba(var(--fx-surface-textrgb, 15, 23, 42), 0.05)" },
                    }}
                  >
                    {copied ? <CheckOutlinedIcon sx={{ fontSize: 15 }} /> : <ContentCopyOutlinedIcon sx={{ fontSize: 15 }} />}
                  </IconButton>
                </Tooltip>
              </Box>

              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: 13,
                  fontFamily: "monospace, monospace",
                  color: colors.gray.base || "var(--fx-surface-text, #0F172A)",
                  wordBreak: "break-all",
                  lineHeight: 1.4,
                  bgcolor: "rgba(var(--fx-surface-basergb, 255, 255, 255), 0.7)",
                  p: 0.75,
                  borderRadius: "6px",
                  border: "1px solid rgba(var(--fx-surface-textrgb, 15, 23, 42), 0.04)",
                }}
              >
                {config.webhookUrl}
              </Typography>
            </Box>
            {Boolean(config.isClientConnected) && (
              <Typography sx={{ color: "#15803d", fontSize: 12, fontWeight: 700 }}>
                Client connected{config.lastPolledAt ? ` • last poll ${new Date(config.lastPolledAt).toLocaleString()}` : ""}
              </Typography>
            )}
          </Box>
        ) : (
          /* UNCONFIGURED / NOT REGISTERED STATE */
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <Chip
                size="small"
                label="Not Configured"
                icon={
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      ml: 1,
                      mr: -0.5,
                      bgcolor: "#94a3b8",
                    }}
                  />
                }
                sx={{
                  height: 26,
                  fontWeight: 700,
                  fontSize: 11,
                  borderRadius: "999px",
                  bgcolor: "rgba(148, 163, 184, 0.12)",
                  color: "var(--fx-surface-textsecondary, #64748B)",
                  border: "1px solid rgba(148, 163, 184, 0.25)",
                  px: 0.5,
                }}
              />
            </Box>

            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1.5,
                background: `linear-gradient(135deg, ${colors.orange[50]} 0%, var(--fx-surface-base, #FFFFFF) 100%)`,
                borderRadius: "16px",
                p: 2,
                border: `1.5px dashed ${colors.orange[200]}`,
                boxShadow: "0 2px 10px rgba(251, 142, 0, 0.04)",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Box
                  sx={{
                    width: 38,
                    height: 38,
                    borderRadius: "10px",
                    bgcolor: "rgba(251, 142, 0, 0.12)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: colors.orange.base,
                  }}
                >
                  <LinkOffOutlinedIcon sx={{ fontSize: 22 }} />
                </Box>
                <Box>
                  <Typography sx={{ color: "var(--fx-surface-text, #0F172A)", fontWeight: 800, fontSize: 14, lineHeight: 1.2 }}>
                    Webhook Setup Required
                  </Typography>
                  <Typography sx={{ color: "var(--fx-surface-textsecondary, #64748B)", fontSize: 12, fontWeight: 500, mt: 0.25 }}>
                    Connect an HTTP endpoint to receive live updates.
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
                <Chip
                  label="⚡ Instant Dispatches"
                  size="small"
                  sx={{
                    fontSize: 11,
                    fontWeight: 700,
                    bgcolor: "var(--fx-surface-base, #FFFFFF)",
                    color: "var(--fx-surface-textsecondary, #64748B)",
                    border: "1px solid rgba(var(--fx-surface-textrgb, 15, 23, 42), 0.08)",
                  }}
                />
                <Chip
                  label="🛡️ HMAC Signature"
                  size="small"
                  sx={{
                    fontSize: 11,
                    fontWeight: 700,
                    bgcolor: "var(--fx-surface-base, #FFFFFF)",
                    color: "var(--fx-surface-textsecondary, #64748B)",
                    border: "1px solid rgba(var(--fx-surface-textrgb, 15, 23, 42), 0.08)",
                  }}
                />
                <Chip
                  label="🔑 API Key Access"
                  size="small"
                  sx={{
                    fontSize: 11,
                    fontWeight: 700,
                    bgcolor: "var(--fx-surface-base, #FFFFFF)",
                    color: "var(--fx-surface-textsecondary, #64748B)",
                    border: "1px solid rgba(var(--fx-surface-textrgb, 15, 23, 42), 0.08)",
                  }}
                />
              </Box>
            </Box>
          </Box>
        )}
      </Box>

      <Box
        data-swapy-no-drag
        sx={{
          display: "flex",
          justify: "end",
          alignItems: "center",
          pt: 1.5,
          mt: "auto",
        }}
      >
        <BaseModalComponent
          modalKey="Organization-EventDelivery-Business"
          modalLabel="Event delivery"
          defaultSizes={{ width: { xs: "95%", md: 1140 }, height: { xs: "92vh", md: 740 } }}
          elementTrigger={
            <Button
              sx={{
                ...(isRegistered ? filledButton : brandButton),
                gap: "6px",
                px: 2,
                py: 0.75,
                height: "38px",
                fontWeight: 700,
              }}
            >
              {isRegistered ? (
                <>
                  <SettingsOutlinedIcon sx={{ fontSize: 16 }} />
                  Manage Event Delivery
                </>
              ) : (
                <>
                  <AddLinkOutlinedIcon sx={{ fontSize: 18 }} />
                  Set Up Webhook Delivery
                </>
              )}
            </Button>
          }
        >
          <EventDeliveryManage />
        </BaseModalComponent>
      </Box>
    </WidgetComponentMaps>
  );
};
